import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./NotificationsPanel.scss";
import NotificationCard from "./NotificationCard";
import FilterDropdownDownArrow from "../../assets/Icons/FilterDropdownDownArrow";
import FilterFunnelIcon from "../../assets/Icons/FilterFunnelIcon";
import FilterItemSelectedTick from "../../assets/Icons/FilterItemSelectedTick";
import FilterDropdownIcon from "../../assets/Icons/FilterDropdownIcon";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import {
  markAsRead,
  markAllAsRead,
  markNotificationsAsRead,
  markNotificationRead,
} from "../../store/slices/notificationsSlice";
import { NOTIFICATIONS, ARIA_LABELS } from "../../util/constants";
import { setOrderChanges } from "../../store/slices/ordersSlice";
import { setAllowedOrderIds } from "../../store/slices/userSlice";
import { ArrowLeftIcon } from "../../assets/Icons/ArrowLeft";
import { OrdersService } from "../../api/services/orders.service";



// Normalizes string value for comparison
const normalize = (val) =>
  String(val ?? "")
    .trim()
    .toLowerCase();

// Extracts category array from notification
const getCategories = (cat) =>
  cat == null ? [] : Array.isArray(cat) ? cat : [cat];

// Slide-out panel displaying user notifications with filtering
export function NotificationsPanel({
  open = false,
  onClose = () => { },
  isLoading = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const panelRef = useRef(null);
  const filterRef = useRef(null);
  const searchInputRef = useRef(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filter, setFilter] = useState("All");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const selectedRole = useSelector((state) => state.user?.selectedRole);

  const { notifications: storeNotifications } = useSelector(
    (state) => state.notifications,
  );

  // Filters out invalid notifications
  const data = useMemo(() => {
    const source = storeNotifications?.length > 0 ? storeNotifications : [];
    // Filter out empty/invalid notifications that lack required fields
    return source.filter((item) => item && item.notificationId && item.heading);
  }, [storeNotifications]);

  // Dispatches read status to Redux store
  const dispatchReadStatus = useCallback(
    (id) => {
      dispatch(markNotificationRead(id));
      dispatch(markNotificationsAsRead([id]));
    },
    [dispatch],
  );

  // Helper to check if a notification is the latest for its orderId
  const isLatestNotificationForOrder = useCallback(
    (notificationId, orderId) => {
      if (!orderId || !data || data.length === 0) return true;

      // Get all notifications for the same orderId
      const sameOrderNotifications = data.filter(
        (n) => n.orderId === orderId || n.orderGuid === orderId,
      );

      if (sameOrderNotifications.length <= 1) return true;

      // Sort by lastUpdated or createdDate descending to find the latest
      const sorted = [...sameOrderNotifications].sort((a, b) => {
        const dateA = new Date(a.lastUpdated || a.createdDate || 0);
        const dateB = new Date(b.lastUpdated || b.createdDate || 0);
        return dateB - dateA;
      });

      // Check if this notification is the latest one
      return sorted[0]?.notificationId === notificationId;
    },
    [data],
  );

  // Handles notification click and navigates to order
  const handleNotificationClick = useCallback(
    async (id, isRead, orderId, orderGuid, changes, category) => {
      // Use existing notification data from the notifications list instead of making API call
      const notificationDetails = data.find(n => n.notificationId === id) || null;

      // Fetch AI-allowed order IDs when opening a notification
      if (selectedRole?.roleId) {
        try {
          const aiOrdersData = await OrdersService.getAIOrders(selectedRole.roleId);
          const rawOrderIds = Array.isArray(aiOrdersData)
            ? aiOrdersData
            : (aiOrdersData?.data?.orderIds || aiOrdersData?.orderIds || []);
          const orderIds = rawOrderIds.map((id) => String(id));
          dispatch(setAllowedOrderIds(orderIds));
        } catch (aiErr) {
          console.warn("Failed to fetch AI order IDs:", aiErr);
        }
      }

      if (orderId) {
        dispatch(setOrderChanges(changes || []));
        // Order fetch is handled by OrdersDetailsHome (orderId change or same-order
        // notificationNavigationId) to avoid duplicate getOrderById calls.

        // Determine source based on category - "Action needed" uses actionNeeded, others use openOrders
        const normalizedCategory = String(category || "")
          .trim()
          .toLowerCase();
        const source =
          normalizedCategory === "action needed"
            ? "actionNeeded"
            : "openOrders";

        // Check if this is the latest notification for the order
        const isLatest = isLatestNotificationForOrder(id, orderGuid || orderId);

        // When category is NOT "action needed" AND this is NOT the latest notification,
        // set a flag to disable chat interactions for non-outlook_agent orders
        const shouldRestrictChat =
          normalizedCategory !== "action needed" && !isLatest;

        navigate(`/orders/${orderGuid}`, {
          state: {
            notificationCategory: category,
            source,
            validationChanges: changes || [],
            fromNotification: true,
            shouldRestrictChat,
            // Unique per click so order-details page + chat refresh even when URL orderId unchanged
            notificationNavigationId: id || `nav-${Date.now()}`,
            // Pass notification details for email summaries and action taken data
            notificationDetails: notificationDetails,
          },
        });
        onClose();
      }
    },
    [
      navigate,
      onClose,
      dispatch,
      selectedRole?.roleId,
      isLatestNotificationForOrder,
      data,
    ],
  );

  // Dismisses notification
  const handleDismissNotification = useCallback(
    (id) => {
      if (!id) return;
      dispatch(markAsRead(id));
      dispatch(markNotificationsAsRead([id]));
    },
    [dispatch],
  );

  // Extracts unique filter options from categories
  const filterOptions = useMemo(() => {
    const allCategories = (data || []).flatMap((n) =>
      getCategories(n?.category),
    );
    const uniqueCategories = [
      ...new Map(
        allCategories
          .filter((c) => c != null && String(c).trim())
          .map((c) => [normalize(c), c]),
      ).values(),
    ].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: "base" }),
    );
    return ["All", ...uniqueCategories];
  }, [data]);

  // Filters notifications based on selected category and search query
  const filteredData = useMemo(() => {
    let items = Array.isArray(data) ? [...data] : [];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      items = items.filter((item) => {
        const heading = normalize(item.heading);
        const orderId = normalize(item.orderId);
        // Also search with AX- prefix for orderId display format
        const orderIdWithPrefix = item.orderId ? normalize(`AX-${item.orderId}`) : "";
        const clientEntity = normalize(item.clientEntity);
        const analyser = normalize(item.analyser);
        const category = getCategories(item.category)
          .map((c) => normalize(c))
          .join(" ");
        return (
          heading.includes(query) ||
          orderId.includes(query) ||
          orderIdWithPrefix.includes(query) ||
          category.includes(query) || clientEntity.includes(query) || analyser.includes(query)
        );
      });
    }

    // Apply category filter
    if (filter !== "All") {
      const normalizedFilter = normalize(filter);
      items = items.filter(({ category: cat }) =>
        getCategories(cat).some((c) => normalize(c) === normalizedFilter),
      );
    }

    return items;
  }, [filter, data, searchQuery]);


  // Handle panel open/close: body scroll lock, escape key, focus management
  useEffect(() => {
    if (!open) {
      document.body.style.overflow = "";
      // Reset search state when panel closes
      setSearchOpen(false);
      setSearchQuery("");
      return;
    }
    const onKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKeyDown);
    setTimeout(() => panelRef.current?.focus(), 0);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // Focus search input when search mode opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handleOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setFilterOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [filterOpen]);

  // Generates unique key for list rendering
  const getNotificationKey = (item) =>
    item.notificationId
      ? `${item.notificationId}-${item.lastUpdated || ""}`
      : item.id || item.title;

  return (
    <>
      {/* Overlay backdrop - closes panel on click */}
      <div
        className={`notifications-overlay ${open ? "open" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
      />

      {/* Main panel container */}
      <aside
        ref={panelRef}
        className={`notifications-panel ${open ? "open" : ""}`}
        role="dialog"
        aria-label={NOTIFICATIONS.TITLE}
        tabIndex={-1}
      >
        {/* Header section - title, search, filter dropdown, and close button */}
        <div className="notif-header">
          {searchOpen ? (
            /* Search mode header */
            <div className="notif-search-header">
              <button
                type="button"
                className="notif-search-back"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery("");
                }}
                aria-label="Close search"
              >
                <ArrowLeftIcon width={20} height={20} color="#A4A3B1" />
              </button>
              <div className="notif-search-input-wrapper">
                <SearchIcon width={16} height={16} color="#A4A3B1" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="notif-search-input"
                  placeholder=""
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="notif-search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <CloseIcon width={16} height={16} color="#7f7f91" />
                  </button>
                )}
              </div>
              {/* Filter icon in search mode */}
              <div className="notif-filter notif-filter-search-mode" ref={filterRef}>
                <button
                  type="button"
                  className="notif-filter-icon-btn"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={filterOpen}
                  aria-label="Filter notifications"
                >
                  <FilterDropdownIcon />
                  {filter !== "All" && <span className="notif-filter-dot" />}
                </button>
                {/* Filter dropdown menu */}
                {filterOpen && (
                  <div className="notif-filter-menu" role="listbox">
                    <div className="notif-filter-head">
                      <span className="icon" aria-hidden="true">
                        <FilterFunnelIcon />
                      </span>
                      <span className="text">{NOTIFICATIONS.FILTER_BY}</span>
                    </div>
                    {filterOptions.map((opt) => (
                      <button
                        key={opt}
                        role="option"
                        title={opt}
                        className={`notif-filter-item ${opt === filter ? "selected" : ""}`}
                        onClick={() => {
                          setFilter(opt);
                          setFilterOpen(false);
                        }}
                      >
                        <span className="label">{opt}</span>
                        {opt === filter && (
                          <span className="check">
                            <FilterItemSelectedTick />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Normal header */
            <>
              <div className="notif-header-left">
                <div className="notif-title">
                  {NOTIFICATIONS.TITLE} ({filteredData.length})
                </div>
              </div>
              <div className="notif-header-right">
                {/* Search button */}
                <button
                  type="button"
                  className="notif-search-btn"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Search notifications"
                >
                  <SearchIcon width={18} height={18} color="#dedee2" />
                </button>
                {/* Filter dropdown */}
                <div className="notif-filter" ref={filterRef}>
                  <button
                    type="button"
                    className="notif-filter-trigger"
                    onClick={() => setFilterOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={filterOpen}
                  >
                    <span className="notif-filter-label">
                      {/* <span className="icon" aria-hidden="true">
                        <FilterDropdownIcon />
                      </span> */}
                      <span className="label">{filter}</span>
                    </span>
                    {filter !== "All" ? (
                      <span
                        className="notif-filter-clear-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilter("All");
                          setFilterOpen(false);
                        }}
                        aria-label="Clear filter"
                        title="Clear filter"
                        role="button"
                      >
                        <CloseIcon width={20} height={20} color="#a4a3b1" />
                      </span>
                    ) : (
                      <span
                        className={`chev ${filterOpen ? "up" : "down"}`}
                        aria-hidden="true"
                      >
                        {/* <FilterDropdownDownArrow /> */}
                        <FilterDropdownIcon />
                      </span>
                    )}
                  </button>
                  {/* Filter dropdown menu */}
                  {filterOpen && (
                    <div className="notif-filter-menu" role="listbox">
                      <div className="notif-filter-head">
                        <span className="icon" aria-hidden="true">
                          <FilterFunnelIcon />
                        </span>
                        <span className="text">{NOTIFICATIONS.FILTER_BY}</span>
                      </div>
                      {filterOptions.map((opt) => (
                        <button
                          key={opt}
                          role="option"
                          title={opt}
                          className={`notif-filter-item ${opt === filter ? "selected" : ""}`}
                          onClick={() => {
                            setFilter(opt);
                            setFilterOpen(false);
                          }}
                        >
                          <span className="label">{opt}</span>
                          {opt === filter && (
                            <span className="check">
                              <FilterItemSelectedTick />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Close button */}
                <button
                  className="notif-close"
                  aria-label={ARIA_LABELS.CLOSE}
                  onClick={onClose}
                >
                  <CloseIcon width="20" height="20" color="#ffffff" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Content section - notification list or empty state */}
        <div className="notif-content">
          {isLoading ? (
            <div className="notif-loading">
              <div className="notif-loading-spinner"></div>
              <span>Loading notifications...</span>
            </div>
          ) : filteredData.length > 0 ? (
            filteredData.map((item) => (
              <NotificationCard
                key={getNotificationKey(item)}
                item={item}
                onClick={handleNotificationClick}
                onDismiss={handleDismissNotification}
              />
            ))
          ) : (
            <div className="notif-item">
              <div className="notif-item-title">
                {NOTIFICATIONS.NO_NOTIFICATIONS_ICON}
              </div>
              <div className="notif-item-sub">
                {NOTIFICATIONS.NO_NOTIFICATIONS_MESSAGE}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default NotificationsPanel;
